import React from 'react';

import { trackAnalyticsEvent } from '@eb/site-analytics';
import gettext from '@eb/gettext';
import { getFormattedDateTime } from '@eb/datetime';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { Tag } from '@eb/eds-tag';
import { Icon } from '@eb/eds-icon';
import { Divider } from '@eb/eds-divider';
import { CalendarRepeatFill } from '@eb/eds-iconography';
import { SelectField } from '@eb/eds-input-field';
import { constants } from '@eb/event-create-publish-utils';

import './index.scss';

const SERIES_OCCURRENCE_SELECT_DATE_FORMAT = 'ddd, l [at] LT';
const DEFAULT_LOCALE = 'en_US';

const {
    ACTION_LEFT_NAV_LINK_CLICKED,
    GA_MANAGE_MY_EVENTS_LABEL,
    GA_MANAGE_CATEGORY,
    MANAGE_MY_EVENTS_LABEL,
    EVENTS_PAGE_PATH,
} = constants;

const EventSeriesChildDisclaimer = ({ eventId }) => (
    <aside
        className="eds-text-bs eds-l-mar-top-4"
        // Needed for links to work in gettext; no user-generated content
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
            __html: gettext(
                `Event information can't be edited on individual occurrences. Select <a href="/myevent?eid=%(eventId)s"}>All occurrences</a> to edit.`,
                {
                    eventId,
                },
            ),
        }}
    />
);

const EventSeriesTag = React.memo(() => (
    <div className="eds-l-mar-top-2">
        <Tag
            isStatic={true}
            backgroundColor="eds-bg-color--grey-300"
            isDisabled={true}
        >
            <span className="eds-align--center eds-text-color--grey-700">
                <span className="eds-l-mar-right-1">
                    <Icon
                        type={<CalendarRepeatFill />}
                        size="small"
                        color="grey-700"
                    />
                </span>
                <p className="eds-text-color--grey-700">
                    {gettext('Recurring event')}
                </p>
            </span>
        </Tag>
    </div>
));

/*
 * Currently custom css is not working on legacy manage pages.
 * The issue is filed with https://jira.evbhome.com/browse/EB-125954
 * Remove this patch once the above ticket is fixed.
 * Using $ui-900.
 */
const NavTitleStyle = () => {
    const styles = {
        __html: `
            .event-nav-description-title {
                color: #1E0A3C; /* Maps to EDS $ui-900 */
            }
        `,
    };

    return (
        <style
            type="text/css"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={styles}
        />
    );
};

/*
 * Currently custom css is not working on legacy manage pages.
 * The issue is filed with https://jira.evbhome.com/browse/EB-125954
 * Remove this patch once the above ticket is fixed.
 */
const SeriesDescriptionStyles = () => {
    /*
     * Removes bottom margin from eds component `eds-structure__left-drawer-description`
     * to make room for select field on series description
     */
    const styles = {
        __html: `
            .event-series-description {
                margin-bottom: -24px;
            }
        `,
    };

    return (
        <style
            type="text/css"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={styles}
        />
    );
};
const ManageMyEventsButtonStyle = () => {
    const styles = {
        __html: `
        .eds-structure__left-drawer-description {
            padding: 0px;
        }
        .eds-nav-list-item.eds-nav-list-item--link:hover {
            background-color:  #EEEDF2;
        }
        .eds-nav-list-item--alt-theme:hover {
            background-color:  #EEEDF2;
        }
        .event-description__switch-event-boton {
            color: #6f7287;
            padding: 24px;
            font-weight: 600;
        }
        .event-description__switch-event-boton:hover {
            cursor: pointer;
            color: #39364f;
            text-decoration: none;
            background:  #EEEDF2;
        }
        .event-description__title {
            padding: 16px 24px 32px 24px;
        }
        .event-description__switch-event-anchor:hover {
            text-decoration: none;
        }
        `,
    };

    return (
        <style
            type="text/css"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={styles}
        />
    );
};
const trackManageMyEventsButton = (eventId) => {
    trackAnalyticsEvent({
        action: ACTION_LEFT_NAV_LINK_CLICKED,
        label: GA_MANAGE_MY_EVENTS_LABEL,
        category: GA_MANAGE_CATEGORY,
        dimensions: { eventId },
    });
};

const ManageMyEventsButton = ({ eventId }) => (
    <a
        href={EVENTS_PAGE_PATH}
        className="event-description__switch-event-anchor"
    >
        <div
            className="event-description__switch-event-boton"
            onClick={() => trackManageMyEventsButton(eventId)}
        >
            <ManageMyEventsButtonStyle />
            {MANAGE_MY_EVENTS_LABEL}
        </div>
    </a>
);

const EventVenue = ({ venueName }) => {
    if (venueName) {
        return (
            <div className="eds-l-pad-top-2">
                <p className="eds-text-bm">{venueName}</p>
            </div>
        );
    }

    return null;
};

const _handleSeriesDateChange = (eventId) => {
    window.location.assign(`/myevent?eid=${eventId}`);
};

const SeriesDatesDropdown = ({ seriesDates, parentId, eventId, locale }) => {
    if (isEmpty(seriesDates)) {
        return null;
    }

    let displayValues = map(
        seriesDates,
        ({ eventId: eid, start, timezone }) => ({
            value: eid,
            display: getFormattedDateTime(
                start,
                false,
                timezone,
                locale,
                SERIES_OCCURRENCE_SELECT_DATE_FORMAT,
            ),
        }),
    );

    displayValues = [
        {
            value: parentId,
            display: gettext('All occurrences'),
        },
        ...displayValues,
    ];

    return (
        <>
            <SeriesDescriptionStyles />
            <div className="eds-l-mar-top-4 series-dates-dropdown">
                <SelectField
                    values={displayValues}
                    disabled={false}
                    label={gettext('Occurrence')}
                    onChange={_handleSeriesDateChange}
                    data-spec="series-dates-dropdown"
                    value={eventId}
                />
            </div>
        </>
    );
};

const EventSeriesParentDescription = ({
    eventName,
    venueName,
    seriesDates,
    eventId,
    parentId,
    locale,
}) => (
    <div
        className="event-series-description"
        data-spec="series-parent-event-side-nav-desc"
    >
        <ManageMyEventsButton eventId={eventId} />
        <Divider />

        <NavTitleStyle />
        <div className="event-description__title">
            <h3 className="eds-text-hs--fixed eds-text-weight--light">
                <a
                    className="event-nav-description-title"
                    href={`/myevent?eid=${eventId}`}
                >
                    {eventName}
                </a>
            </h3>

            <EventSeriesTag />

            <SeriesDatesDropdown
                seriesDates={seriesDates}
                parentId={parentId}
                eventId={eventId}
                locale={locale}
            />

            <EventVenue venueName={venueName} />
        </div>
    </div>
);

const EventSeriesChildDescription = ({
    eventName,
    venueName,
    seriesDates,
    parentId,
    eventId,
    locale,
}) => (
    <div
        className="event-series-description"
        data-spec="series-child-event-side-nav-desc"
    >
        <ManageMyEventsButton eventId={eventId} />
        <Divider />
        <NavTitleStyle />
        <div className="event-description__title">
            <h3 className="eds-text-hs--fixed eds-text-weight--light">
                <a
                    className="event-nav-description-title"
                    href={`/myevent?eid=${parentId}`}
                >
                    {eventName}
                </a>
            </h3>

            <SeriesDatesDropdown
                seriesDates={seriesDates}
                parentId={parentId}
                eventId={eventId}
                locale={locale}
            />

            <EventVenue venueName={venueName} />

            <EventSeriesChildDisclaimer eventId={parentId} />
        </div>
    </div>
);

const EventDescription = ({ eventName, eventId, eventDate, venueName }) => (
    <div
        data-automation="coyote-sidebar-description"
        data-spec="event-side-nav-desc"
        className="event-description"
    >
        <ManageMyEventsButton eventId={eventId} />
        <Divider />
        <div className="event-description__title">
            <NavTitleStyle />
            <h3 className="eds-text-hs--fixed eds-text-weight--light">
                <a
                    className="event-nav-description-title"
                    href={`/myevent?eid=${eventId}`}
                >
                    {eventName}
                </a>
            </h3>
            <p className="eds-l-pad-top-2">{eventDate}</p>

            <EventVenue venueName={venueName} />
        </div>
    </div>
);

const SideDrawerDescription = ({
    eventName,
    eventDate,
    venueName,
    isSeriesParent,
    isSeriesChild,
    seriesDates = [],
    parentId,
    eventId,
    locale = DEFAULT_LOCALE,
}) => {
    if (isSeriesParent) {
        return (
            <EventSeriesParentDescription
                eventName={eventName}
                venueName={venueName}
                seriesDates={seriesDates}
                eventId={eventId}
                parentId={parentId}
                locale={locale}
            />
        );
    } else if (isSeriesChild) {
        return (
            <EventSeriesChildDescription
                eventName={eventName}
                venueName={venueName}
                seriesDates={seriesDates}
                parentId={parentId}
                eventId={eventId}
                locale={locale}
            />
        );
    }

    return (
        <EventDescription
            eventName={eventName}
            eventDate={eventDate}
            venueName={venueName}
            eventId={eventId}
        />
    );
};

export default SideDrawerDescription;
